import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Phone, 
  MessageCircle, 
  ChevronRight, 
  Star, 
  Award, 
  Sparkles,
  Users,
  ArrowRight
} from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
// Before/After Slider Component
function BeforeAfterSlider({ before, after, title }) {
  const [sliderPosition, setSliderPosition] = useState(50);

  return (
    <div className="relative overflow-hidden rounded-sm aspect-[4/5]">
      <img
        src={after}
        alt="After"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img
          src={before}
          alt="Before"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: `${100 / (sliderPosition / 100)}%` }}
        />
      </div>
      <div
        className="absolute inset-y-0 cursor-ew-resize"
        style={{ left: `${sliderPosition}%`, transform: "translateX(-50%)" }}
      >
        <div className="w-1 h-full bg-white shadow-lg" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
          <ChevronRight size={16} className="text-obsidian -mr-1" />
          <ChevronRight size={16} className="text-obsidian rotate-180 -ml-1" />
        </div>
      </div>
      <input
        type="range"
        min="5"
        max="95"
        value={sliderPosition}
        onChange={(e) => setSliderPosition(Number(e.target.value))}
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
      />
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
        <p className="text-white text-sm font-medium">{title}</p>
      </div>
      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium">
        Before
      </div>
      <div className="absolute top-4 right-4 bg-gold text-white px-3 py-1 rounded-full text-xs font-medium">
        After
      </div>
    </div>
  );
}

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [beforeAfter, setBeforeAfter] = useState([]);
  const [loading, setLoading] = useState(true);

 useEffect(() => {
  // Static mode: no backend, no API calls
  setSettings({
    salon_name: "Illuminate Salon",
    hero_subheading: "Experience luxury hair & beauty services in the heart of Amritsar",
    hero_image: "https://images.unsplash.com/photo-1728488447889-13f95adcf5a0?w=1920",
  });

  setServices([
    {
      id: 1,
      name: "Haircut & Styling",
      category: "Hair",
      description: "Precision haircut, wash & blow-dry styling.",
      price: "₹499+",
      duration: "45 min",
      image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800",
    },
    {
      id: 2,
      name: "Hair Color",
      category: "Color",
      description: "Global color, highlights & premium finishing.",
      price: "₹1499+",
      duration: "90 min",
      image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=800",
    },
    {
      id: 3,
      name: "Facial & Cleanup",
      category: "Skin",
      description: "Glow facial, cleanup and skin nourishment.",
      price: "₹799+",
      duration: "60 min",
      image: "https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=800",
    },
  ]);

  setGallery([
    { id: 1, url: "https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?w=800", caption: "Salon" },
    { id: 2, url: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=800", caption: "Hair" },
    { id: 3, url: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800", caption: "Styling" },
    { id: 4, url: "https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=800", caption: "Facial" },
    { id: 5, url: "https://images.unsplash.com/photo-1526045478516-99145907023c?w=800", caption: "Care" },
    { id: 6, url: "https://images.unsplash.com/photo-1522338140500-5902f0a0d1f0?w=800", caption: "Makeup" },
  ]);

  setReviews([
    { id: 1, name: "Simran", rating: 5, text: "Amazing service and super friendly staff!" },
    { id: 2, name: "Harpreet", rating: 5, text: "Loved the haircut and the ambience. Highly recommended." },
    { id: 3, name: "Jasleen", rating: 4, text: "Great experience. Very professional work." },
    { id: 4, name: "Gurpreet", rating: 5, text: "Best salon experience in the area!" },
  ]);

  setBeforeAfter([]);
  setLoading(false);
}, []);


  if (loading) {
    return (
      <div className="min-h-screen">
        <Skeleton className="h-screen w-full" />
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={settings?.hero_image || "https://images.unsplash.com/photo-1728488447889-13f95adcf5a0?w=1920"}
            alt="Illuminate Salon"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>

        <div className="relative z-10 text-center text-white section-padding max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.3em] text-gold mb-6"
          >
            Welcome to
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-medium tracking-tight mb-6"
          >
            {settings?.salon_name || "Illuminate Salon"}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-white/80 font-light max-w-2xl mx-auto mb-10"
          >
            {settings?.hero_subheading || "Experience luxury hair & beauty services in the heart of Amritsar"}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/book" className="btn-gold" data-testid="hero-book-btn">
              Book Appointment
            </Link>
            <a href="tel:+917837774865" className="btn-secondary border-white text-white hover:bg-white hover:text-obsidian flex items-center gap-2" data-testid="hero-call-btn">
              <Phone size={18} />
              Call Now
            </a>
            <a
              href="https://wa.me/917837774865"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white hover:text-gold transition-colors"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle size={18} />
              WhatsApp
            </a>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-2 bg-white rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Services Preview */}
      <section className="section-padding section-gap bg-white" data-testid="services-preview">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">What We Offer</p>
          <h2 className="font-heading text-4xl md:text-5xl tracking-tight">
            Our Services
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(Array.isArray(services) ? services : []).map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative overflow-hidden bg-white border border-neutral-100 hover:border-gold/30 transition-all duration-500"
              data-testid={`service-card-${index}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={service.image || "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400"}
                  alt={service.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <p className="text-xs uppercase tracking-widest text-gold mb-2">{service.category}</p>
                <h3 className="font-heading text-xl mb-2">{service.name}</h3>
                <p className="text-sm text-neutral-500 mb-4 line-clamp-2">{service.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-gold font-medium">{service.price}</span>
                  {service.duration && (
                    <span className="text-xs text-neutral-400">{service.duration}</span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/services" className="btn-secondary inline-flex items-center gap-2" data-testid="view-all-services-btn">
            View All Services <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="section-padding section-gap bg-neutral-50" data-testid="gallery-preview">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Our Work</p>
          <h2 className="font-heading text-4xl md:text-5xl tracking-tight">
            Gallery
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {gallery.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="aspect-square overflow-hidden group cursor-pointer"
              data-testid={`gallery-item-${index}`}
            >
              <img
                src={item.url}
                alt={item.caption || "Gallery"}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/gallery" className="btn-secondary inline-flex items-center gap-2" data-testid="view-gallery-btn">
            View Full Gallery <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Before/After Section */}
      {beforeAfter.length > 0 && (
        <section className="section-padding section-gap bg-white" data-testid="before-after-section">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Transformations</p>
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight">
              Before & After
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {beforeAfter.map((item, index) => (
              <BeforeAfterSlider
                key={item.id}
                before={item.before_image}
                after={item.after_image}
                title={item.title}
              />
            ))}
          </div>
        </section>
      )}

      {/* Reviews Section */}
      <section className="section-padding section-gap bg-obsidian text-white" data-testid="reviews-section">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Testimonials</p>
          <h2 className="font-heading text-4xl md:text-5xl tracking-tight">
            What Our Clients Say
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {reviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/5 border border-white/10 p-8 rounded-sm"
              data-testid={`review-card-${index}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} size={16} className="fill-gold text-gold" />
                ))}
              </div>
              <p className="text-white/80 mb-6 font-accent italic text-lg leading-relaxed">
                "{review.text}"
              </p>
              <p className="text-gold font-medium">{review.name}</p>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/reviews" className="btn-gold inline-flex items-center gap-2" data-testid="view-reviews-btn">
            Read More Reviews <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding py-24 bg-gold text-white text-center" data-testid="cta-section">
        <h2 className="font-heading text-4xl md:text-5xl tracking-tight mb-6">
          Ready to Transform?
        </h2>
        <p className="text-white/90 max-w-xl mx-auto mb-10 text-lg">
          Book your appointment today and experience the Illuminate difference.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/book" className="bg-obsidian text-white hover:bg-neutral-800 rounded-full px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300 font-medium" data-testid="cta-book-btn">
            Book Appointment
          </Link>
          <a href="tel:+917837774865" className="border-2 border-white text-white hover:bg-white hover:text-gold rounded-full px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300 font-medium" data-testid="cta-call-btn">
            Call: 078377 74865
          </a>
        </div>
      </section>
    </div>
  );
}
